import { useEffect, useRef, useState } from 'react';
import { Camera, Mail, X } from 'lucide-react';
import { Button, Description, Drawer, Label, useOverlayState } from '@heroui/react';
import { AsyncButton } from '../../../components/ui/async-button';
import { DrawerTextAreaField } from '../../../components/ui/drawer-form-field';
import { ImageFilePicker } from '../../../components/ui/image-file-picker';
import {
  drawerBodyClassName,
  drawerFooterClassName,
  sideDrawerDialogClassName,
} from '../../../components/ui/side-drawer-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { uploadSupportReportPhoto } from '../../../lib/firebase-storage';
import {
  SUPPORT_REPORT_CATEGORIES,
  buildSupportReportMailto,
  getSupportEmail,
  type SupportReportCategoryId,
} from '../../../lib/support';
import { getErrorMessage } from '../../../lib/toast';

type OverlayState = ReturnType<typeof useOverlayState>;

interface ReportProblemDrawerProps {
  state: OverlayState;
  userUid?: string | null;
  userEmail?: string | null;
}

export function ReportProblemDrawer({
  state,
  userUid,
  userEmail,
}: ReportProblemDrawerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [categoryId, setCategoryId] = useState<SupportReportCategoryId>('bug');
  const [description, setDescription] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supportEmail = getSupportEmail();
  const canSubmit = description.trim().length > 0;

  useEffect(() => {
    if (!state.isOpen) return;
    setCategoryId('bug');
    setDescription('');
    setPhotoFile(null);
    setPhotoPreview(null);
    setError(null);
    setSubmitting(false);
  }, [state.isOpen]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handlePhotoChange = (file: File | null) => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(file ? URL.createObjectURL(file) : null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('Descreva o problema para continuar.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      let attachmentUrl: string | undefined;
      if (photoFile) {
        try {
          attachmentUrl = await uploadSupportReportPhoto(photoFile);
        } catch (uploadError) {
          handlePhotoChange(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          setError(
            `${getErrorMessage(uploadError, 'Não foi possível enviar a imagem.')} Remova o anexo ou tente de novo.`,
          );
          setSubmitting(false);
          return;
        }
      }

      const href = buildSupportReportMailto({
        categoryId,
        description,
        userUid,
        userEmail,
        attachmentUrl,
      });
      state.close();
      window.location.href = href;
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível abrir o e-mail de suporte.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>Ajuda / Reportar problema</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>

            <Drawer.Body className={drawerBodyClassName}>
              <Description>
                Conte o que aconteceu. Em seguida, o app abre o seu cliente de e-mail para enviar a
                mensagem para o suporte.
              </Description>

              <StatusCallout status="info" title="Seu e-mail vai abrir">
                Ao enviar, abrimos o aplicativo de e-mail do dispositivo com a mensagem pronta para{' '}
                <span className="font-medium">{supportEmail}</span>. Você só precisa confirmar o
                envio.
              </StatusCallout>

              {error ? <StatusCallout status="danger">{error}</StatusCallout> : null}

              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">Categoria</Label>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Categoria">
                  {SUPPORT_REPORT_CATEGORIES.map((category) => {
                    const selected = categoryId === category.id;
                    return (
                      <Button
                        key={category.id}
                        type="button"
                        size="sm"
                        variant={selected ? 'primary' : 'secondary'}
                        aria-pressed={selected}
                        isDisabled={submitting}
                        onPress={() => setCategoryId(category.id)}
                      >
                        {category.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              <DrawerTextAreaField
                label="Descrição do problema"
                value={description}
                onChange={setDescription}
                placeholder="Ex.: ao tentar reservar a vaga, o botão não responde."
                rows={5}
                isDisabled={submitting}
                description="Obrigatório. Inclua o que você tentou fazer e o que viu na tela."
              />

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Imagem (opcional)</span>
                {photoPreview ? (
                  <div className="relative overflow-hidden rounded-xl border border-separator">
                    <img
                      src={photoPreview}
                      alt="Prévia do anexo"
                      className="max-h-48 w-full object-cover"
                    />
                    <Button
                      type="button"
                      isIconOnly
                      size="sm"
                      variant="secondary"
                      className="absolute right-2 top-2"
                      isDisabled={submitting}
                      onPress={() => {
                        handlePhotoChange(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ) : (
                  <ImageFilePicker
                    inputRef={fileInputRef}
                    disabled={submitting}
                    aria-label="Adicionar imagem"
                    onFile={(file) => handlePhotoChange(file)}
                  >
                    <Button type="button" variant="secondary" isDisabled={submitting}>
                      <Camera size={16} />
                      Adicionar imagem
                    </Button>
                  </ImageFilePicker>
                )}
                <Description>
                  A imagem é enviada para o armazenamento do app e o link entra no e-mail (até 5
                  MB).
                </Description>
              </div>
            </Drawer.Body>

            <Drawer.Footer className={drawerFooterClassName}>
              <Button
                type="button"
                variant="secondary"
                isDisabled={submitting}
                onPress={() => state.close()}
              >
                Cancelar
              </Button>
              <AsyncButton
                variant="primary"
                isPending={submitting}
                pendingLabel="Preparando"
                isDisabled={!canSubmit}
                onPress={() => void handleSubmit()}
              >
                <Mail size={16} />
                Abrir e-mail
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
